import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type AuthUser = {
  authenticated: true;
  kickUserId: string;
  kickUsername: string;
  role: "Admin" | "Visitor";
  /** Email z Kick OAuth userinfo (jeśli udostępniony) — prefill /buy + admin testów. */
  email: string | null;
};

export type AuthState =
  | { loading: true }
  | { loading: false; user: AuthUser | null };

type AuthContextValue = AuthState & {
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const API = (path: string) =>
  `${import.meta.env.VITE_API_BASE_URL || ""}${path}`;

async function fetchMe(): Promise<AuthUser | null> {
  try {
    const r = await fetch(API("/api/auth/me"), { credentials: "include" });
    if (!r.ok) return null;
    const data = await r.json();
    if (!data?.authenticated) return null;
    return {
      authenticated: true,
      kickUserId: data.kickUserId ?? "",
      kickUsername: data.kickUsername ?? "",
      role: data.role === "Admin" ? "Admin" : "Visitor",
      email: data.email ?? null,
    };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ loading: true });

  async function refresh() {
    setState({ loading: true });
    const user = await fetchMe();
    setState({ loading: false, user });
  }

  async function logout() {
    try {
      await fetch(API("/api/auth/logout"), { method: "POST", credentials: "include" });
    } catch {}
    setState({ loading: false, user: null });
  }

  useEffect(() => {
    refresh();
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export const KICK_LOGIN_URL = API("/api/auth/kick/login");
export const KICK_BROADCASTER_CONNECT_URL = API("/api/auth/kick/admin/connect");
